import type { AppDeps } from './deps';
import { loginFlow } from './loginFlow';

/**
 * A phone can be passed on. When someone different from the person this device
 * last served logs in, what that person left behind goes first: their queued
 * captures, their chosen books, everything cached for them. Then the device
 * remembers the new person. Both ways in (the code, the chat link) await this
 * before they navigate. Everything the wipe covers lives here, in one place.
 */
export async function forgetAnotherPerson({ client, outbox, queryClient, persister }: Pick<AppDeps, 'client' | 'outbox' | 'queryClient' | 'persister'>, personId: string): Promise<void> {
  const last = await client.lastPerson.get();
  if (last !== null && last !== personId) {
    await outbox.clearAll();
    await client.preferredOrg.set(null);
    // The new person starts at Home, not on the last person's screen.
    loginFlow.rememberPlace('');
    // Everything cached goes. Exactly ['me'] stays as an object (the screens already watch it) and is overwritten with the new person at once.
    queryClient.removeQueries({ predicate: (q) => !(q.queryKey.length === 1 && q.queryKey[0] === 'me') });
    queryClient.getMutationCache().clear();
    // The saved copy on the phone goes too (Review Focus 5); failing to delete it must not stop the login.
    try {
      await persister?.removeClient();
    } catch {
      // The copy may outlive this wipe, but the login goes on.
    }
  }
  await client.lastPerson.set(personId);
}
